import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, ChevronLeft, ChevronRight, RotateCcw, Plus, X, CalendarDays, Pencil, Stethoscope } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { usePersistedState, useEphemeralState } from '../../lib/persist.js'
import { useMe } from '../../lib/me.js'
import { pickableBranches as pickBranches } from '../../lib/branch.js'
import { nameInitial } from '../../lib/text.js'
import SearchableSelect from '../../components/SearchableSelect.jsx'
import DoctorShiftEditor from '../../components/schedule/DoctorShiftEditor.jsx'
import {
  WEEK_HEAD, monthCells, monthRange, fmtMonthTH, fmtDayTH, thisMonth, shiftMonth, isSameMonth,
  todayKey, asList, groupByDay, doctorLabel, doctorTitleOf, startTs, endTs, fmtTime, fmtDuration,
  chipStyle, relabelBranch, isShiftRow, durationMin,
} from '../../lib/doctorSchedule.js'

const SELECT = 'rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-700 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

// สนามเป้า (#1) is closed and superseded by อารีย์ (#8) — the same branch the
// booking calendar hides. Kept out of the picker so nobody rosters into it.

function Avatar({ name, img }) {
  if (img) return <img src={img} alt="" className="w-7 h-7 rounded-lg object-cover ring-1 ring-black/5 shrink-0" referrerPolicy="no-referrer" />
  return (
    <span className="w-7 h-7 rounded-lg bg-brand-50 text-brand-700 ring-1 ring-brand-100 flex items-center justify-center text-[12px] font-semibold shrink-0">
      {nameInitial(name)}
    </span>
  )
}

/* One shift inside a day cell. Compact by construction: a month grid has seven
 * of these side by side, so the code and the window are all that fit — the rest
 * lives in the day panel below. */
function ShiftChip({ row, label, onOpen }) {
  const style = chipStyle(row.event_color)
  return (
    <button type="button" title={`${label} · ${fmtTime(startTs(row))}–${fmtTime(endTs(row))}`}
      onClick={(e) => { e.stopPropagation(); onOpen(row) }}
      className={`block w-full text-left text-[11.5px] leading-tight rounded border px-1 py-0.5 truncate transition ${
        style ? 'text-slate-700 hover:brightness-95' : 'bg-sky-50 border-sky-100 text-slate-700 hover:bg-sky-100'}`}
      style={style || undefined}>
      <span className="font-semibold">{label}</span>{' '}
      <span className="text-slate-500">
        {row.all_day ? 'ทั้งวัน' : `${fmtTime(startTs(row))}–${fmtTime(endTs(row))}`}
      </span>
    </button>
  )
}

function DayCell({ dayKey, rows, byId, selected, onSelect, onAdd, onOpen }) {
  const isToday = dayKey === todayKey()
  return (
    <div role="button" tabIndex={0} onClick={() => onSelect(dayKey)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(dayKey) } }}
      className={`group relative min-h-[124px] border-b border-r border-slate-100 p-1.5 text-left flex flex-col gap-1 cursor-pointer transition-colors ${
        selected ? 'bg-brand-50/70' : 'hover:bg-brand-50/40'}`}>
      <div className="flex items-center justify-between">
        <span className={`text-[13px] font-semibold ${isToday
          ? 'inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-600 text-white'
          : 'text-slate-700 pl-1'}`}>{Number(dayKey.slice(8))}</span>
        {/* Muted until the cell is hovered — a plus on all 35 cells would out-shout
            the roster it sits on top of. */}
        <button type="button" title="เพิ่มเวรวันนี้"
          onClick={(e) => { e.stopPropagation(); onAdd(dayKey) }}
          className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded-md text-slate-400 hover:bg-white hover:text-brand-600">
          <Plus size={13} />
        </button>
      </div>
      <div className="space-y-0.5">
        {rows.map((r) => <ShiftChip key={r.id} row={r} label={doctorLabel(r, byId)} onOpen={onOpen} />)}
      </div>
    </div>
  )
}

function DayPanel({ dayKey, rows, byId, branchName, onClose, onAdd, onOpen }) {
  return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-slate-100">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 ring-1 ring-brand-100 flex items-center justify-center shrink-0">
            <CalendarDays size={15} />
          </span>
          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-slate-900 truncate">{fmtDayTH(dayKey)}</div>
            <div className="text-[12.5px] text-slate-500">{branchName} · {rows.length} เวร</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => onAdd(dayKey)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700">
            <Plus size={14} /> เพิ่มเวร
          </button>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100" title="ปิด"><X size={16} /></button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-[13.5px] text-slate-400">ยังไม่มีทันตแพทย์ออกตรวจในวันนี้</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="text-left text-[12px] font-medium text-slate-400 border-b border-slate-100">
                <th className="px-5 py-2.5">ทันตแพทย์</th>
                <th className="px-4 py-2.5">ช่วงเวลา</th>
                <th className="px-4 py-2.5">รายละเอียด</th>
                <th className="px-4 py-2.5 w-px" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => onOpen(r)} className="border-b border-slate-50 last:border-0 hover:bg-brand-50/40 cursor-pointer transition">
                  <td className="px-5 py-3 align-top">
                    <div className="flex items-center gap-2.5">
                      <Avatar name={doctorLabel(r, byId)} img={r.doctor?.profile_img} />
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate">{doctorLabel(r, byId)}</div>
                        <div className="text-[12px] text-slate-400 truncate">#{r.doctor_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="tabular-nums text-slate-700">
                      {r.all_day ? 'ทั้งวัน' : `${fmtTime(startTs(r))} – ${fmtTime(endTs(r))}`}
                    </div>
                    {/* Legacy Bubble rows carry duration: 0 with a perfectly good
                        window, so the window wins and the column falls back. */}
                    <div className="text-[12px] text-slate-400">
                      {fmtDuration(durationMin(startTs(r), endTs(r)) || r.duration)}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top text-slate-600">
                    {r.details ? <span className="break-words">{r.details}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    <span className="inline-flex items-center gap-1 text-[12.5px] text-slate-400"><Pencil size={13} /> แก้ไข</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function DoctorSchedule() {
  const me = useMe()
  const [branches, setBranches] = useState([])
  const [doctors, setDoctors] = useState([])
  const [refErr, setRefErr] = useState('')

  const [anchor, setAnchor] = useEphemeralState('sched:doc:month', () => thisMonth())
  const [branchId, setBranchId] = usePersistedState('sched:doc:branch', '')
  const [doctorId, setDoctorId] = usePersistedState('sched:doc:doctor', '')

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const reqId = useRef(0)

  const [notice, setNotice] = useState('')     // partial-success warning from a write
  const [selectedDay, setSelectedDay] = useState(null)
  const [editing, setEditing] = useState(null)   // { row } | { dayKey }
  const [tick, setTick] = useState(0)            // bumped after a save → refetch

  useEffect(() => {
    Promise.allSettled([cachedGet('/api/branches'), cachedGet('/api/doctors', { slim: 1 })])
      .then(([br, dr]) => {
        if (br.status === 'fulfilled') setBranches(asList(br.value))
        if (dr.status === 'fulfilled') setDoctors(asList(dr.value).filter((d) => d.active !== false))
        if (br.status === 'rejected' || dr.status === 'rejected') setRefErr('โหลดรายชื่อสาขา/ทันตแพทย์ไม่สำเร็จ')
      })
  }, [])

  const pickableBranches = useMemo(
    () => pickBranches(branches),
    [branches],
  )

  // Xano's timetable query REQUIRES a branch, so the page opens on the user's own
  // rather than on an empty screen. A session whose branch is the closed one
  // falls through to the first pickable branch instead of selecting nothing.
  useEffect(() => {
    if (branchId || !pickableBranches.length) return
    const mine = pickableBranches.find((b) => Number(b.id) === Number(me?.branch_id))
    setBranchId(String((mine || pickableBranches[0]).id))
  }, [pickableBranches, me?.branch_id]) // eslint-disable-line react-hooks/exhaustive-deps

  const doctorById = useMemo(() => {
    const m = new Map()
    for (const d of doctors) m.set(Number(d.id), d)
    return m
  }, [doctors])

  const doctorOptions = useMemo(() => doctors.map((d) => ({
    value: String(d.id),
    label: doctorTitleOf(d) || `หมอ #${d.id}`,
    hint: [d.name_eng, d.surname, (d.spec || []).join(' ')].filter(Boolean).join(' · '),
  })).sort((a, b) => a.label.localeCompare(b.label, 'th')), [doctors])

  const filterOptions = useMemo(
    () => [{ value: '', label: 'ทุกท่าน' }, ...doctorOptions],
    [doctorOptions],
  )

  const range = useMemo(() => monthRange(anchor), [anchor])

  useEffect(() => {
    if (!branchId) { setRows([]); return }
    const id = ++reqId.current
    setLoading(true); setErr('')
    apiGet('/api/hr/schedule/doctor', { branch_id: branchId, start_date: range.start, end_date: range.end })
      .then((r) => { if (id === reqId.current) setRows(asList(r).filter(isShiftRow)) })
      .catch((e) => { if (id === reqId.current) { setRows([]); setErr(String(e.message || e)) } })
      .finally(() => { if (id === reqId.current) setLoading(false) })
  }, [branchId, range.start, range.end, tick])

  // Changing the month can strand the day panel on a day that is no longer shown.
  useEffect(() => {
    if (selectedDay && !selectedDay.startsWith(range.start.slice(0, 7))) setSelectedDay(null)
  }, [range.start, selectedDay])

  const byDay = useMemo(() => groupByDay(rows, { doctorId }), [rows, doctorId])
  const cells = useMemo(() => monthCells(anchor), [anchor])

  const stats = useMemo(() => {
    let shifts = 0
    const docs = new Set()
    for (const list of byDay.values()) { shifts += list.length; list.forEach((r) => docs.add(Number(r.doctor_id))) }
    return { shifts, doctors: docs.size, days: byDay.size }
  }, [byDay])

  const branchName = useMemo(() => {
    const b = branches.find((x) => String(x.id) === String(branchId))
    return b ? relabelBranch(b.name) : '—'
  }, [branches, branchId])

  // `res` is only present on a delete, and only carries anything when the shift
  // went but one of its ห้ามแทรก breaks would not — a partial success the
  // operator has to see, and which the drawer cannot show because it is closing.
  const onSaved = (res) => {
    setNotice(res?.warning || '')
    setEditing(null)
    setTick((t) => t + 1)
  }
  // A shift the drawer just created stays open on its own record: บล็อกเวลา and
  // ผู้ช่วย both attach to a shift that exists, and closing here would make the
  // operator hunt for the chip they only just made to add either.
  const onCreated = (created) => { setEditing({ row: created }); setTick((t) => t + 1) }

  return (
    <div className="max-w-[1280px]">
      <p className="text-[13.5px] text-slate-500 mb-5">
        ตารางออกตรวจของทันตแพทย์รายเดือน — คลิกวันเพื่อดูรายละเอียด หรือคลิกที่ชื่อหมอเพื่อแก้ไขเวร
      </p>

      {/* month */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button onClick={() => setAnchor(shiftMonth(anchor, -1))}
          className="p-2 rounded-lg text-slate-500 ring-1 ring-slate-200 bg-white hover:bg-slate-50" title="เดือนก่อนหน้า">
          <ChevronLeft size={15} />
        </button>
        <div className="min-w-[170px] text-center text-[15px] font-semibold text-slate-800">{fmtMonthTH(anchor)}</div>
        <button onClick={() => setAnchor(shiftMonth(anchor, 1))}
          className="p-2 rounded-lg text-slate-500 ring-1 ring-slate-200 bg-white hover:bg-slate-50" title="เดือนถัดไป">
          <ChevronRight size={15} />
        </button>
        {!isSameMonth(anchor, thisMonth()) && (
          <button onClick={() => setAnchor(thisMonth())}
            className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100">
            <RotateCcw size={14} /> เดือนนี้
          </button>
        )}
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2.5 mb-4">
        <select value={branchId} onChange={(e) => { setBranchId(e.target.value); setSelectedDay(null) }} className={SELECT}>
          <option value="">เลือกสาขา…</option>
          {pickableBranches.map((b) => <option key={b.id} value={b.id}>{relabelBranch(b.name)}</option>)}
        </select>
        <SearchableSelect
          options={filterOptions} value={doctorId} onChange={setDoctorId}
          placeholder="ทุกท่าน" searchPlaceholder="พิมพ์ชื่อหมอ…" className="min-w-[220px]"
        />
        {doctorId && (
          <button onClick={() => setDoctorId('')} className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100">
            <X size={14} /> ล้าง
          </button>
        )}
        <div className="ml-auto flex items-center gap-3 text-[12.5px] text-slate-400">
          {loading && <span className="inline-flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> กำลังโหลด…</span>}
          {!loading && !err && branchId && (
            <span><b className="text-slate-600">{stats.shifts}</b> เวร · <b className="text-slate-600">{stats.doctors}</b> ท่าน · {stats.days} วัน</span>
          )}
        </div>
      </div>

      {refErr && <div className="mb-3 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-[12.5px] text-amber-800">{refErr}</div>}
      {notice && (
        <div className="mb-3 flex items-start gap-2 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-[12.5px] text-amber-800">
          <span className="flex-1">{notice}</span>
          <button onClick={() => setNotice('')} className="shrink-0 opacity-60 hover:opacity-100">✕</button>
        </div>
      )}

      <div className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-100 text-center text-[12.5px] font-medium text-slate-500">
          {WEEK_HEAD.map((d) => <div key={d} className="py-2">{d}</div>)}
        </div>

        {!branchId ? (
          <div className="px-4 py-14 text-center text-[14px] text-slate-400">เลือกสาขาก่อนเพื่อดูตารางออกตรวจ</div>
        ) : err ? (
          <div className="px-4 py-14 text-center text-[14px] text-rose-500">{err}</div>
        ) : (
          <div className="grid grid-cols-7">
            {cells.map((key, i) => (
              key == null
                ? <div key={`p${i}`} className="min-h-[124px] border-b border-r border-slate-100 bg-slate-50/40" />
                : (
                  <DayCell
                    key={key} dayKey={key} rows={byDay.get(key) || []} byId={doctorById}
                    selected={selectedDay === key}
                    onSelect={(k) => setSelectedDay((cur) => (cur === k ? null : k))}
                    onAdd={(k) => setEditing({ dayKey: k })}
                    onOpen={(row) => setEditing({ row })}
                  />
                )
            ))}
          </div>
        )}

        {!loading && !err && branchId && stats.shifts === 0 && (
          <div className="px-4 py-4 text-center text-[13px] text-slate-400 border-t border-slate-100">
            <Stethoscope size={15} className="inline mr-1.5 -mt-0.5" />
            ไม่มีเวรออกตรวจในเดือนนี้{doctorId ? ' สำหรับหมอที่เลือก' : ''}
          </div>
        )}
      </div>

      {selectedDay && (
        <DayPanel
          dayKey={selectedDay} rows={byDay.get(selectedDay) || []} byId={doctorById}
          branchName={branchName}
          onClose={() => setSelectedDay(null)}
          onAdd={(k) => setEditing({ dayKey: k })}
          onOpen={(row) => setEditing({ row })}
        />
      )}

      {editing && (
        <DoctorShiftEditor
          row={editing.row || null}
          dayKey={editing.dayKey || selectedDay || todayKey()}
          branchId={branchId}
          doctorOptions={doctorOptions}
          doctorById={doctorById}
          branches={pickableBranches}
          monthRows={rows}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
          onCreated={onCreated}
        />
      )}
    </div>
  )
}
